import React from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, MapPin, Briefcase, Users } from 'lucide-react';
import { scrollToTop } from '../../utils/scroll';
import { InterviewPreview } from '../../types/interviews';

interface InterviewCardProps {
  interview: InterviewPreview;
}

export function InterviewCard({ interview }: InterviewCardProps) {
  const handleClick = () => {
    scrollToTop();
  };

  return (
    <Link
      to={`/interviews/${interview.id}`}
      onClick={handleClick}
      className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden transition-all duration-300 transform hover:-translate-y-2"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={interview.cover}
          alt={interview.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.src.includes('fallback')) {
              target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2670&auto=format&fit=crop';
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        <div className="absolute top-3 left-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-teal-500/90 to-cyan-500/90 backdrop-blur-md rounded-full shadow-lg">
            <Users className="w-4 h-4 text-white drop-shadow" />
            <span className="text-xs font-semibold text-white drop-shadow">
              Recit PVT
            </span>
          </div>
        </div>

        <div className="absolute bottom-3 left-3 right-3">
          <h2 className="text-base sm:text-lg font-bold text-white line-clamp-2 leading-snug sm:leading-snug drop-shadow-lg">
            {interview.title}
          </h2>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <img
            src={interview.interviewee.avatar || `https://ui-avatars.com/api/?name=${interview.interviewee.firstname}&background=random`}
            alt={interview.interviewee.firstname}
            className="w-10 h-10 rounded-full ring-2 ring-teal-100 dark:ring-teal-900/30"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {interview.interviewee.firstname} {interview.interviewee.lastname}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
              {interview.interviewee.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-teal-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {interview.interviewee.location}
                  </span>
                </div>
              )}
              {interview.interviewee.occupation && (
                <div className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3 text-teal-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {interview.interviewee.occupation}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4 leading-relaxed">
          {interview.shortDescription}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 font-medium">
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>
              {format(parseISO(interview.created), 'dd MMM yyyy', { locale: fr })}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <img
              src={interview.author.avatar || `https://ui-avatars.com/api/?name=${interview.author.firstname}&background=random`}
              alt={interview.author.firstname}
              className="w-6 h-6 rounded-full ring-2 ring-gray-100 dark:ring-gray-700"
            />
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {interview.author.firstname}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 ring-2 ring-teal-500/0 group-hover:ring-teal-500/50 rounded-2xl transition-all duration-300 pointer-events-none" />
    </Link>
  );
}
